import { auth } from "@/firebase";
import useFirestoreUser from "@/hooks/users/useFirestoreUser";
import { ApiResponse } from "@/types/api";
import { User } from "@/types/User";
import { FirestoreUser } from "@tunagari/shared/types/User";
import { User as FirebaseUser } from "firebase/auth";
import { createContext, useContext, useEffect, useState } from "react";
import { KeyedMutator } from "swr";

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  refreshUser: KeyedMutator<ApiResponse<FirestoreUser | null>>;
  isAdmin: boolean;
  isLoading: boolean;
  isError: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const {
    user: firestoreUser,
    isLoading: firestoreLoading,
    isError: firestoreError,
    refresh,
  } = useFirestoreUser();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      user?.getIdTokenResult().then((idTokenResult) => {
        if (idTokenResult.claims.admin) {
          setIsAdmin(true);
        }
      });
      setFirebaseUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (firestoreError) {
      setError(true);
    }
  }, [firestoreError]);

  const combinedUser =
    firebaseUser && firestoreUser
      ? ({ ...firebaseUser, ...firestoreUser } as User)
      : null;

  return (
    <AuthContext.Provider
      value={{
        user: combinedUser,
        firebaseUser,
        refreshUser: refresh,
        isAdmin,
        isLoading: loading || firestoreLoading,
        isError: error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
